import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import './index.scss';
import reportWebVitals from './reportWebVitals';
import GridViewIcon from '@mui/icons-material/GridView';
import GroupIcon from '@mui/icons-material/Group';
import PaymentsIcon from '@mui/icons-material/Payments';
import InsightsIcon from '@mui/icons-material/Insights';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import logo from './images/logo-long.svg';
import phone from './images/phone.png';
import iphoneTemplate from './images/iphone-template.png';
import playsportLogo from './images/partners/Playsport-logo.png';
import kickitoutLogo from './images/partners/kick-it-out-logo.png';
import playerMockup1 from './images/mockups/for-the-players1.png';
import playerMockup2 from './images/mockups/for-the-players2.png';
import playerMockup3 from './images/mockups/for-the-players3.png';
import parentMockup1 from './images/mockups/for-the-parents1.png';
import parentMockup2 from './images/mockups/for-the-parents2.png';
import parentMockup3 from './images/mockups/for-the-parents3.png';
import coachMockup1 from './images/mockups/for-the-coaches1.png';
import coachMockup2 from './images/mockups/for-the-coaches2.png';
import coachMockup3 from './images/mockups/for-the-coaches3.png';
import coachMockup4 from './images/mockups/for-the-coaches4.png';
import App from './App';

// Custom arrow components for the slider
const PrevArrow = (props) => {
  const { className, onClick } = props;
  return (
    <div className={`${className} custom-arrow custom-prev-arrow`} onClick={onClick}>
      <ArrowBackIosNewIcon />
    </div>
  );
};

const NextArrow = (props) => {
  const { className, onClick } = props;
  return (
    <div className={`${className} custom-arrow custom-next-arrow`} onClick={onClick}>
      <ArrowForwardIosIcon />
    </div>
  );
};

// Renamed from App to MainContent
function MainContent() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [emailError, setEmailError] = useState('');
  const [timeLeft, setTimeLeft] = useState({});
  
  // Create refs for the sliders
  const playerSliderRef = (slider) => {
    if (slider !== null) {
      window.playerSlider = slider;
    }
  };
  
  const coachSliderRef = (slider) => {
    if (slider !== null) {
      window.coachSlider = slider;
    }
  };
  
  const parentSliderRef = (slider) => {
    if (slider !== null) {
      window.parentSlider = slider;
    }
  };
  
  // Add touch handling for carousels
  useEffect(() => {
    // Ensure slick sliders are properly initialized
    const carousels = document.querySelectorAll('.phone-carousel');
    
    carousels.forEach(carousel => {
      // Make images non-draggable to prevent interference with swipe
      const images = carousel.querySelectorAll('img');
      images.forEach(img => {
        img.draggable = false;
      });
      
      // Force slider to refresh to pick up touch events
      const slider = carousel.querySelector('.slick-slider');
      if (slider) {
        // Force refresh slick to apply touch events
        setTimeout(() => {
          // This will trigger a DOM reflow
          slider.style.display = 'none';
          // Force reflow - properly access offsetHeight
          void slider.offsetHeight;
          slider.style.display = '';
        }, 100);
      }
    });
  }, []);
  
  // Slider settings - not used currently but keeping for future use
  // eslint-disable-next-line no-unused-vars
  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: false,
    autoplaySpeed: 5000,
    pauseOnHover: true,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          arrows: false,
          swipe: true,
          swipeToSlide: true
        }
      }
    ]
  };
  
  // Phone carousel settings - base settings
  const basePhoneSliderSettings = {
    dots: true,
    infinite: true,
    speed: 300,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: false,
    pauseOnHover: true,
    arrows: false,
    cssEase: 'ease-out',
    swipe: true,
    swipeToSlide: true,
    touchMove: true,
    touchThreshold: 20,
    waitForAnimate: false
  };
  
  // Create random settings for each slider
  const playerSliderSettings = {
    ...basePhoneSliderSettings,
    autoplaySpeed: 3500 + Math.floor(Math.random() * 1000), // 3.5-4.5s
    autoplay: false,
    initialSlide: Math.floor(Math.random() * 3) // random initial slide
  };
  
  const coachSliderSettings = {
    ...basePhoneSliderSettings,
    autoplaySpeed: 4000 + Math.floor(Math.random() * 1500), // 4-5.5s
    autoplay: false,
    initialSlide: Math.floor(Math.random() * 4) // random initial slide
  };
  
  const parentSliderSettings = {
    ...basePhoneSliderSettings,
    autoplaySpeed: 3000 + Math.floor(Math.random() * 2000), // 3-5s
    autoplay: false,
    initialSlide: Math.floor(Math.random() * 3) // random initial slide
  };
  
  // Calculate countdown to April 30, 2025
  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = new Date('April 30, 2025') - new Date();
      
      if (difference > 0) {
        return {
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        };
      }
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    };
    
    // Initial calculation
    setTimeLeft(calculateTimeLeft());
    
    // Set up timer
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    
    // Clean up timer
    return () => clearInterval(timer);
  }, []); // Empty dependency array - runs only on mount
  
  // Add reCAPTCHA execution
  const executeRecaptcha = async () => {
    try {
      const token = await window.grecaptcha.execute('6LdleMEqAAAAACjtGkwMXYO2fQXJoc-VpRQXSjpu', { action: 'submit' });
      return token;
    } catch (error) {
      console.error('reCAPTCHA error:', error);
      return null;
    }
  };

  const validateEmail = (email) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const email = e.target.email.value.trim();
    
    // Reset states
    setEmailError('');
    setSubmitStatus(null);

    // Validate email
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      // Get reCAPTCHA token
      const recaptchaToken = await executeRecaptcha();
      
      if (!recaptchaToken) {
        throw new Error('Failed to verify reCAPTCHA');
      }

      const response = await fetch('/api/send-email.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email,
          recaptchaToken 
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to join waitlist');
      }

      setSubmitStatus('success');
      e.target.reset();
    } catch (error) {
      setSubmitStatus('error');
      console.error('Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="app-container">
      <header>
        <svg 
          className="bokeh" 
          viewBox="0 0 10 10" 
          xmlns="http://www.w3.org/2000/svg" 

            >
          
          <circle cx="10%" cy="85%" r="75%"  />
          <circle cx="45%" cy="50%" r="15%"   />
          <circle cx="85%" cy="35%" r="30%"  />
          <circle cx="60%" cy="85%" r="20%"   />
          <circle cx="45%" cy="50%" r="30%"   />
          <circle cx="35%" cy="25%" r="20%"   />
          <circle cx="90%" cy="-25%" r="35%"  />
          <circle cx="-15%" cy="30%" r="30%"  />
          <circle cx="65%" cy="85%" r="55%"   />
          <circle cx="45%" cy="50%" r="20%"   />

        </svg>
      </header>
      <main>
        <div className="content">
          <img src={logo} alt="FirstWhistle" className="logo logo-mobile" />
          <div className="hero-section">
            <div className="hero-content">
              <img src={logo} alt="FirstWhistle" className="logo logo-desktop" />
              
              <div className="video-showcase mobile-video">
                <video 
                  className="promo-video" 
                  controls 
                  loop 
                  playsInline
                  preload="auto"
                >
                  <source src="/videos/firstwhistle-promo.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              
              <h1>Built for Players.<br/>Powered by Coaches.<br/>Backed by Parents.</h1>
              <h3>Introducing the <span>First AI-Powered</span> Football Team Manager</h3>
              <p className="intro">
                Say goodbye to chaos and hello to smarter team management with FirstWhistle, the first-ever AI-powered app designed specifically for grassroots football. Whether you're managing player availability, tracking fees, or recruiting new players, our intelligent tools take the stress out of organisation.
              </p>
              <form className="waitlist-form" onSubmit={handleSubmit}>
                <div className="input-group">
                  <input 
                    type="email" 
                    name="email"
                    placeholder="Enter your email" 
                    required 
                    disabled={isSubmitting}
                    autoComplete="email"
                    aria-label="Email address for waitlist"
                    onChange={() => {
                      setEmailError('');
                      setSubmitStatus(null);
                    }}
                  />
                  {emailError && <span className="error-message">{emailError}</span>}
                  {submitStatus === 'success' && (
                    <span className="success-message">Thanks for joining! We'll be in touch soon.</span>
                  )}
                  {submitStatus === 'error' && (
                    <span className="error-message">Sorry, something went wrong. Please try again.</span>
                  )}
                </div>
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  aria-label="Submit email for waitlist"
                >
                  {isSubmitting ? 'Joining...' : 'Get Early Access'}
                </button>
              </form>
            </div>
            <div className="hero-image">
              <img src={phone} alt="FirstWhistle App" />
            </div>
          </div>

          <div className="features">
            <div className="feature-grid">
              <div className="feature-card">
                <GridViewIcon className="feature-icon" />
                <h3>Smarter Team Selection</h3>
                <p>Let AI help you optimise lineups and formations.</p>
              </div>
              
              <div className="feature-card">
                <GroupIcon className="feature-icon" />
                <h3>Track Player Availability</h3>
                <p>Know exactly who's in or out for every match.</p>
              </div>
              
              <div className="feature-card">
                <PaymentsIcon className="feature-icon" />
                <h3>Simplify Payments</h3>
                <p>Easily collect and track player fees.</p>
              </div>
              
              <div className="feature-card">
                <InsightsIcon className="feature-icon" />
                <h3>AI-Driven Insights</h3>
                <p>Get recommendations for improving team performance.</p>
              </div>
              
              <div className="feature-card">
                <EmojiEventsIcon className="feature-icon" />
                <h3>Celebrate Success</h3>
                <p>Leaderboards and stats keep you and your players motivated.</p>
              </div>
            </div>
            
            <div className="video-showcase desktop-video">
              <video 
                className="promo-video" 
                controls 
                loop 
                playsInline
                preload="auto"
              >
                <source src="/videos/firstwhistle-promo.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
            
            <p className="cta-text">Be the first to experience how AI can transform your game. Join now!</p>
            
            <div className="partners">
              <div className="partner-logos">
                <a href="https://playsport.co.uk" target="_blank" rel="noopener noreferrer"><img src={playsportLogo} alt="Playsport" /></a>
                <a href="https://kickitout.co.uk" target="_blank" rel="noopener noreferrer"><img src={kickitoutLogo} alt="Kick it out" /></a>
              </div>
            </div>
            
            <div className="audience-section">
              <div className="audience-columns">
                <div className="audience-column">
                  <div className="phone-carousel">
                    <Slider ref={playerSliderRef} {...playerSliderSettings} key="player-slider">
                      <div className="phone-slide">
                        <img src={playerMockup1} alt="FirstWhistle for Players - Dashboard" className="audience-phone" draggable="false" />
                      </div>
                      <div className="phone-slide">
                        <img src={playerMockup2} alt="FirstWhistle for Players - Stats" className="audience-phone" draggable="false" />
                      </div>
                      <div className="phone-slide">
                        <img src={playerMockup3} alt="FirstWhistle for Players - Awards" className="audience-phone" draggable="false" />
                      </div>
                    </Slider>
                  </div>
                  <h3>For the <span style={{ color: '#ffd700' }}>Players</span></h3>
                  <p>Track your stats, stay in the loop, and earn your place. FirstWhistle gives you a clear view of your progress – and the recognition you deserve.</p>
                  <ul className="feature-list">
                    <li><CheckCircleOutlineIcon className="check-icon" /> Match notifications</li>
                    <li><CheckCircleOutlineIcon className="check-icon" /> Player stats and rankings</li>
                    <li><CheckCircleOutlineIcon className="check-icon" /> Team selections</li>
                    <li><CheckCircleOutlineIcon className="check-icon" /> MOTM voting and rewards</li>
                  </ul>
                </div>
                <div className="audience-column">
                  <div className="phone-carousel">
                    <Slider ref={coachSliderRef} {...coachSliderSettings} key="coach-slider">
                      <div className="phone-slide">
                        <img src={coachMockup1} alt="FirstWhistle for Coaches - Team Selection" className="audience-phone" draggable="false" />
                      </div>
                      <div className="phone-slide">
                        <img src={coachMockup2} alt="FirstWhistle for Coaches - Analytics" className="audience-phone" draggable="false" />
                      </div>
                      <div className="phone-slide">
                        <img src={coachMockup3} alt="FirstWhistle for Coaches - Availability" className="audience-phone" draggable="false" />
                      </div>
                      <div className="phone-slide">
                        <img src={coachMockup4} alt="FirstWhistle for Coaches - Availability" className="audience-phone" draggable="false" />
                      </div>
                    </Slider>
                  </div>
                  <h3>For the <span style={{ color: '#ffd700' }}>Coaches</span></h3>
                  <p>Simplify team management with tools that take care of the admin. Focus on coaching while FirstWhistle handles availability, performance, and communication.</p>
                  <ul className="feature-list">
                    <li><CheckCircleOutlineIcon className="check-icon" /> AI-powered team selection</li>
                    <li><CheckCircleOutlineIcon className="check-icon" /> Live player availability</li>
                    <li><CheckCircleOutlineIcon className="check-icon" /> Performance tracking</li>
                    <li><CheckCircleOutlineIcon className="check-icon" /> Match reports and feedback tools</li>
                  </ul>
                </div>
                <div className="audience-column">
                  <div className="phone-carousel">
                    <Slider ref={parentSliderRef} {...parentSliderSettings} key="parent-slider">
                      <div className="phone-slide">
                        <img src={parentMockup1} alt="FirstWhistle for Parents - Fixtures" className="audience-phone" draggable="false" />
                      </div>
                      <div className="phone-slide">
                        <img src={parentMockup2} alt="FirstWhistle for Parents - Payments" className="audience-phone" draggable="false" />
                      </div>
                      <div className="phone-slide">
                        <img src={parentMockup3} alt="FirstWhistle for Parents - Development" className="audience-phone" draggable="false" />
                      </div>
                    </Slider>
                  </div>
                  <h3>For the <span style={{ color: '#ffd700' }}>Parents</span></h3>
                  <p>Get the updates that matter – from fixtures and player awards to performance insights. Stay connected to your child's football journey, every step of the way.</p>
                  <ul className="feature-list">
                    <li><CheckCircleOutlineIcon className="check-icon" /> Fixture notifications</li>
                    <li><CheckCircleOutlineIcon className="check-icon" /> One-tap location and time details</li>
                    <li><CheckCircleOutlineIcon className="check-icon" /> Coach feedback summaries</li>
                    <li><CheckCircleOutlineIcon className="check-icon" /> Team news and updates</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="limited-offer">
              <div className="limited-offer-content">
                <h3>Limited Time Offer</h3>
                <p><span>Only 2 places left</span> before our waitlist closes. Join today to secure your spot!</p>
                <div className="countdown-timer">
                  <div className="countdown-item">
                    <span className="countdown-value">{timeLeft.days}</span>
                    <span className="countdown-label">Days</span>
                  </div>
                  <div className="countdown-item">
                    <span className="countdown-value">{timeLeft.hours}</span>
                    <span className="countdown-label">Hours</span>
                  </div>
                  <div className="countdown-item">
                    <span className="countdown-value">{timeLeft.minutes}</span>
                    <span className="countdown-label">Mins</span>
                  </div>
                  <div className="countdown-item">
                    <span className="countdown-value">{timeLeft.seconds}</span>
                    <span className="countdown-label">Secs</span>
                  </div>
                </div>
                <form className="waitlist-form-secondary" onSubmit={handleSubmit}>
                  <div className="input-group">
                    <input 
                      type="email" 
                      name="email"
                      placeholder="Enter your email" 
                      required 
                      disabled={isSubmitting}
                      autoComplete="email"
                      aria-label="Email address for waitlist"
                      onChange={() => {
                        setEmailError('');
                        setSubmitStatus(null);
                      }}
                    />
                  </div>
                  <button 
                    type="submit" 
                    disabled={isSubmitting}
                    aria-label="Submit email for waitlist"
                  >
                    {isSubmitting ? 'Joining...' : 'Get Early Access'}
                  </button>
                </form>
              </div>
            </div>
            
            <div className="faq-section">
              <h2>Frequently Asked Questions</h2>
              <div className="faq-grid">
                <div className="faq-item">
                  <h3>What is FirstWhistle?</h3>
                  <p>FirstWhistle is an AI-powered football team management app designed specifically for grassroots teams, helping with player availability, payments, and team selection.</p>
                </div>
                <div className="faq-item">
                  <h3>Is FirstWhistle free to use?</h3>
                  <p>FirstWhistle will be available with both free and premium features. Join our waitlist to be first to know about our launch pricing.</p>
                </div>
                <div className="faq-item">
                  <h3>How does the AI team selection work?</h3>
                  <p>Our AI analyses player statistics, availability, and performance data to suggest optimal team formations and lineups, helping managers make informed decisions.</p>
                </div>
                <div className="faq-item">
                  <h3>Can I track player payments through the app?</h3>
                  <p>Yes, FirstWhistle includes a built-in payment tracking system to manage match fees, subscriptions, and other team-related expenses.</p>
                </div>
                <div className="faq-item">
                  <h3>What platforms will FirstWhistle be available on?</h3>
                  <p>FirstWhistle will be available on both iOS and Android devices via a web interface. You can bookmark it so that it appears as a native iOS / Google app. It will also be available on a desktop via firstwhistle.app.</p>
                </div>
                <div className="faq-item">
                  <h3>How secure is my team's data?</h3>
                  <p>We take data security seriously. All team and player information is encrypted and stored securely following industry best practices.</p>
                </div>
                <div className="faq-item">
                  <h3>Can multiple team managers use the same account?</h3>
                  <p>Yes, FirstWhistle supports multiple admin roles, allowing different team officials to manage various aspects of the team.</p>
                </div>
                <div className="faq-item">
                  <h3>When will FirstWhistle be available?</h3>
                  <p>FirstWhistle is currently in development. Join our waitlist to be among the first to access the platform when we launch and receive exclusive early-bird offers.</p>
                </div>
              </div>
            </div>
            
            <p className="small-text">FirstWhistle is an AI-powered football team management application tailored for grassroots teams. Our features include tracking player availability, managing payments, viewing leaderboards, accessing player statistics, recruiting new players, and receiving timely notifications for team selections. Simplify your football management experience with FirstWhistle.</p>
          </div>
        </div>
      </main>
    </div>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
